import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Truck, MapPin } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";

const SHIPPING_FEES = { postage: 7.95, collection: 0 };
const FREE_POSTAGE_THRESHOLD = 100;

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const [form, setForm] = useState({
    full_name: user?.name || "",
    guest_email: user?.email || "",
    address_line1: "",
    address_line2: "",
    city: "",
    postcode: "",
    country: "United Kingdom",
    phone: "",
    research_use_confirmed: false,
    notes: "",
    shipping_method: "postage",
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <h2 className="font-display text-3xl">Your cart is empty.</h2>
        <button onClick={() => navigate("/shop")} className="mt-4 px-5 py-3 bg-[#ff1493] text-white border-[3px] border-black shadow-brut btn-press font-mono-ui text-xs font-bold">
          BROWSE PRODUCTS
        </button>
      </div>
    );
  }

  const upd = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.research_use_confirmed) {
      toast.error("Please confirm the research-use-only declaration.");
      return;
    }
    setLoading(true);
    try {
      const shipping = {
        full_name: form.full_name,
        address_line1: isCollection ? "Collection in person" : form.address_line1,
        address_line2: isCollection ? "" : form.address_line2,
        city: isCollection ? "N/A" : form.city,
        postcode: isCollection ? "N/A" : form.postcode,
        country: form.country,
        phone: form.phone,
        research_use_confirmed: form.research_use_confirmed,
        shipping_method: form.shipping_method,
      };
      let data;
      if (user) {
        const r = await api.post("/orders", { items, shipping, notes: form.notes });
        data = r.data;
      } else {
        if (!form.guest_email) {
          toast.error("Please enter your email address.");
          setLoading(false);
          return;
        }
        const r = await api.post("/orders/guest", {
          items,
          shipping,
          notes: form.notes,
          guest_email: form.guest_email,
          guest_name: form.full_name,
        });
        data = r.data;
      }
      const { data: _unused } = { data };
      void _unused;
      clear();
      toast.success(`Order #${data.id.slice(0, 8).toUpperCase()} placed — pending payment.`);
      navigate(user ? "/account" : "/");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const freePostage = subtotal >= FREE_POSTAGE_THRESHOLD;
  const postageFee = freePostage ? 0 : SHIPPING_FEES.postage;
  const total = subtotal + (form.shipping_method === "collection" ? 0 : postageFee);
  const isCollection = form.shipping_method === "collection";

  return (
    <div data-testid="checkout-page" className="min-h-screen">
      <section className="border-b-[3px] border-black">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
          <div className="font-mono-ui text-xs text-[#ff1493]">— SECURE CHECKOUT</div>
          <h1 className="font-display text-5xl md:text-6xl leading-none mt-3">Checkout</h1>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-8 py-10 grid lg:grid-cols-3 gap-8">
        <form onSubmit={submit} className="lg:col-span-2 border-[3px] border-black bg-white p-6 md:p-8 shadow-brut space-y-5">
          <div>
            <div className="font-display text-2xl mb-3">Fulfilment</div>
            {!freePostage && (
              <div className="mb-3 font-mono-ui text-[10px] bg-[#facc15] border-[3px] border-black px-3 py-2 inline-block">
                SPEND £{(FREE_POSTAGE_THRESHOLD - subtotal).toFixed(2)} MORE TO UNLOCK FREE UK POSTAGE
              </div>
            )}
            {freePostage && (
              <div className="mb-3 font-mono-ui text-[10px] bg-[#ff1493] text-white border-[3px] border-black px-3 py-2 inline-block font-bold">
                ✨ FREE UK POSTAGE UNLOCKED
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-3">
              <label className={`cursor-pointer border-[3px] border-black p-4 flex items-start gap-3 ${form.shipping_method === "postage" ? "bg-[#ff1493] text-white shadow-brut-sm" : "bg-white"}`}>
                <input
                  type="radio"
                  name="shipping_method"
                  value="postage"
                  checked={form.shipping_method === "postage"}
                  onChange={upd("shipping_method")}
                  data-testid="ship-postage"
                  className="mt-1 w-4 h-4 accent-black"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Truck className="w-4 h-4" strokeWidth={2.5} />
                    <div className="font-display text-lg leading-none">UK Postage</div>
                  </div>
                  <div className={`font-mono-ui text-[10px] mt-2 ${form.shipping_method === "postage" ? "text-white/90" : "text-black/60"}`}>TRACKED 24–48h · DISCREET PACKAGING</div>
                  {freePostage ? (
                    <div className="mt-2 flex items-baseline gap-2">
                      <div className="font-display text-xl line-through opacity-60">£7.95</div>
                      <div className="font-display text-xl text-[#facc15]">FREE</div>
                    </div>
                  ) : (
                    <div className="font-display text-xl mt-2">£7.95</div>
                  )}
                </div>
              </label>
              <label className={`cursor-pointer border-[3px] border-black p-4 flex items-start gap-3 ${form.shipping_method === "collection" ? "bg-[#00d4ff] text-black shadow-brut-sm" : "bg-white"}`}>
                <input
                  type="radio"
                  name="shipping_method"
                  value="collection"
                  checked={form.shipping_method === "collection"}
                  onChange={upd("shipping_method")}
                  data-testid="ship-collection"
                  className="mt-1 w-4 h-4 accent-black"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" strokeWidth={2.5} />
                    <div className="font-display text-lg leading-none">Collection in person</div>
                  </div>
                  <div className="font-mono-ui text-[10px] mt-2 text-black/60">BY APPOINTMENT · GLASGOW</div>
                  <div className="font-display text-xl mt-2">FREE</div>
                </div>
              </label>
            </div>
          </div>

          <div className="font-display text-2xl pt-2">{isCollection ? "Contact details" : "Shipping details"}</div>
          {!user && (
            <div className="border-[3px] border-black bg-[#00d4ff] p-4 font-mono-ui text-[11px] leading-relaxed flex items-center justify-between gap-3 flex-wrap">
              <span>CHECKING OUT AS A GUEST. ORDER UPDATES WILL BE EMAILED TO THE ADDRESS BELOW.</span>
              <Link to="/login" state={{ from: "/checkout" }} className="font-bold underline shrink-0">SIGN IN INSTEAD</Link>
            </div>
          )}
          <Input label="Full name" value={form.full_name} onChange={upd("full_name")} testid="co-name" required />
          {!user && (
            <Input label="Email" type="email" value={form.guest_email} onChange={upd("guest_email")} testid="co-guest-email" required />
          )}
          {!isCollection && (
            <>
              <Input label="Address line 1" value={form.address_line1} onChange={upd("address_line1")} testid="co-addr1" required />
              <Input label="Address line 2 (optional)" value={form.address_line2} onChange={upd("address_line2")} testid="co-addr2" />
              <div className="grid sm:grid-cols-3 gap-4">
                <Input label="City" value={form.city} onChange={upd("city")} testid="co-city" required />
                <Input label="Postcode" value={form.postcode} onChange={upd("postcode")} testid="co-postcode" required />
                <Input label="Country" value={form.country} onChange={upd("country")} testid="co-country" required />
              </div>
            </>
          )}
          <Input label="Phone" value={form.phone} onChange={upd("phone")} testid="co-phone" required />

          <div>
            <label className="font-mono-ui text-[10px] block mb-1">ORDER NOTES</label>
            <textarea rows={3} value={form.notes} onChange={upd("notes")} data-testid="co-notes" className="w-full border-[3px] border-black px-3 py-2 font-mono-ui text-xs resize-none focus:outline-none" />
          </div>

          <label className="flex items-start gap-3 border-[3px] border-black bg-[#facc15] p-4">
            <input
              type="checkbox"
              checked={form.research_use_confirmed}
              onChange={upd("research_use_confirmed")}
              data-testid="co-research-confirm"
              className="w-5 h-5 mt-0.5 accent-black"
            />
            <span className="text-xs font-mono-ui leading-relaxed">
              I CONFIRM I AM A QUALIFIED RESEARCHER AND THESE PRODUCTS ARE FOR IN-VITRO LABORATORY RESEARCH ONLY. NOT FOR HUMAN OR ANIMAL CONSUMPTION.
            </span>
          </label>

          <div className="border-[3px] border-black bg-[#00d4ff] p-4 font-mono-ui text-[11px] leading-relaxed">
            PAYMENT IS HANDLED OFFLINE. ONCE YOU PLACE THIS ORDER WE WILL EMAIL YOU WITHIN 1 WORKING HOUR WITH BANK TRANSFER / INVOICE DETAILS. YOUR ORDER STAYS IN &ldquo;PENDING&rdquo; UNTIL PAYMENT IS CLEARED.
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="co-place-order"
            className="w-full px-5 py-4 bg-[#ff1493] text-white border-[3px] border-black shadow-brut btn-press font-mono-ui text-xs font-bold disabled:opacity-60"
          >
            {loading ? "PLACING ORDER…" : `PLACE ORDER · £${total.toFixed(2)}`}
          </button>
        </form>

        <aside className="h-fit border-[3px] border-black bg-white p-6 shadow-brut space-y-3">
          <div className="font-display text-2xl mb-2">Order</div>
          {items.map((it) => (
            <div key={it.product_id} className="flex items-center gap-3 border-b-2 border-black/10 pb-2">
              <img src={it.image_url} alt={it.name} className="w-12 h-12 object-cover border-[2px] border-black" />
              <div className="flex-1 min-w-0">
                <div className="font-mono-ui text-[11px] truncate">{it.name}</div>
                <div className="font-mono-ui text-[10px] text-black/50">× {it.quantity}</div>
              </div>
              <div className="font-display text-sm">£{(it.price * it.quantity).toFixed(2)}</div>
            </div>
          ))}
          <div className="font-mono-ui text-sm pt-2 space-y-1">
            <div className="flex justify-between"><span>Subtotal</span><span>£{subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between">
              <span>{isCollection ? "Collection" : "UK Shipping"}</span>
              <span>{isCollection || freePostage ? "FREE" : `£${SHIPPING_FEES.postage.toFixed(2)}`}</span>
            </div>
            <div className="border-t-[3px] border-black mt-2 pt-2 flex justify-between font-display text-xl">
              <span>Total</span><span className="text-[#ff1493]">£{total.toFixed(2)}</span>
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
}

function Input({ label, testid, ...rest }) {
  return (
    <div>
      <label className="font-mono-ui text-[10px] block mb-1">{label.toUpperCase()}</label>
      <input {...rest} data-testid={testid} className="w-full border-[3px] border-black px-3 py-2 font-mono-ui text-xs focus:outline-none" />
    </div>
  );
}
